import React, { useState } from 'react';
import ThumbIcon from 'shared/components/ui/ThumbIcon';

/**
 * The give-feedback form: thumbs, a rating, a comment. Each save is a new
 * feedback event (the latest wins wherever one value is shown). Styled like
 * the eval "Correct?" form beside it. Posts through the parent's `onSubmit`.
 */
export default function RunFeedbackControl({ onSubmit, onCancel, disabled }) {
  const [satisfaction, setSatisfaction] = useState(null);
  const [rating, setRating] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    if (disabled || busy) return;
    if (!satisfaction && rating === '' && !comment.trim()) { setError('Pick Good or Bad, a rating, or a comment.'); return; }
    setError(null);
    setBusy(true);
    try {
      await onSubmit({
        satisfaction: satisfaction || null,
        rating: rating === '' ? null : Number(rating),
        comment: comment.trim() || null,
      });
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not save.');
      setBusy(false);
    }
  };

  const choice = (active) => `inline-flex flex-1 items-center justify-center gap-1.5 rounded border px-2 py-1 ${active
    ? 'border-[color:var(--border-strong)] bg-surface-hover font-semibold text-[color:var(--text-primary)]'
    : 'border-[color:var(--border-default)] text-[color:var(--text-secondary)] hover:bg-surface-hover'}`;
  const field = 'rounded border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] text-[11px] text-[color:var(--text-primary)] outline-none focus:border-[color:var(--border-strong)]';

  return (
    <div className="space-y-2 rounded-md border border-[color:var(--border-default)] bg-surface p-2 text-[11px]">
      <div className="flex items-center gap-1">
        {[['positive', 'Good', 'up'], ['negative', 'Bad', 'down']].map(([value, label, direction]) => (
          <button key={value} type="button" disabled={disabled || busy} aria-pressed={satisfaction === value}
            onClick={() => setSatisfaction((s) => (s === value ? null : value))} className={choice(satisfaction === value)}>
            <ThumbIcon direction={direction} filled={satisfaction === value} size={12} />
            {label}
          </button>
        ))}
        <label className="ml-1 inline-flex items-center gap-1 text-[color:var(--text-muted)]">
          Rating
          <input type="number" min="0" max="10" step="0.5" placeholder="—" value={rating} disabled={disabled || busy}
            onChange={(e) => setRating(e.target.value)} className={`${field} w-12 px-1.5 py-1 tabular-nums`} />
        </label>
      </div>
      <textarea value={comment} disabled={disabled || busy} onChange={(e) => setComment(e.target.value)} rows={3}
        maxLength={4000} placeholder="Comment" aria-label="Comment" className={`${field} w-full p-1.5`} />
      {error && <div className="text-[color:var(--feedback-error)]">{error}</div>}
      <div className="flex items-center gap-1">
        <button type="button" disabled={disabled || busy} onClick={save}
          className="rounded bg-brand px-2 py-1 font-medium text-ink-onBrand hover:bg-brand-hover disabled:opacity-40">
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} className="rounded px-2 py-1 text-[color:var(--text-muted)] hover:bg-surface-hover">Cancel</button>
      </div>
    </div>
  );
}
